import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PLATFORM_NAME, errMsg } from "@/lib/api";
import { homeForRole } from "@/App";
import { PasskeyUnlock } from "@/components/PasskeyUnlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login(form);
      navigate(homeForRole(data?.user?.role));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rise">
        <Link to="/" className="flex items-center gap-2.5 font-display text-lg font-semibold">
          <img src="/limolinkx-logo.png" alt="" className="h-8 w-8 rounded bg-white/95 object-contain p-0.5" />
          <span className="gold-text">{PLATFORM_NAME}</span>
        </Link>
        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-50">Operator sign in</h1>
        <p className="mt-3 text-sm text-slate-400">Dispatch, manifests and fleet in one place.</p>
        <form onSubmit={submit} className="mt-8 space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Email</label>
            <Input data-testid="login-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-12 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Password</label>
            <Input data-testid="login-password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="h-12 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="login-submit" type="submit" disabled={busy} className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
          <PasskeyUnlock scope="office" email={form.email}
            onSignedIn={(data) => navigate(homeForRole(data?.user?.role))} />
          <p className="text-center text-sm text-slate-500">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-gold">Forgot your password?</Link>
          </p>
          <p className="text-center text-sm text-slate-500">
            New operator? <Link to="/signup" data-testid="to-signup-link" className="text-gold">Create a free account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
